import React from "react";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { fmtINR, fmtDate } from "@/lib/format";

/**
 * Generic "click-any-row" detail Sheet. Renders every non-null field of the
 * `row` as a key/value pair — auto-formatting numbers as ₹ and dates as
 * "12 Jul 2026". Nested objects/arrays are pretty-printed as JSON.
 *
 * Usage:
 *   const [detail, setDetail] = useState(null);
 *   <tr onClick={() => setDetail(row)}>...</tr>
 *   <RowDetailSheet row={detail} onClose={() => setDetail(null)} title="Waitlist" />
 */
const HIDE_KEYS = new Set(["_id", "org_id", "branch_id", "created_by",
  "id", "updated_at", "receipt_voucher_id", "collected_by"]);
const MONEY_KEYS = /(amount|price|total|balance|tax|charges|premium|idv|deduction|refund|road_tax|paid|due|cost|value)/i;
const DATE_KEYS = /(_at|_date|_till|_from|_to|effective_from|effective_to|date|when)$/;

function renderValue(k, v) {
  if (v === null || v === undefined || v === "") return <span className="text-muted-foreground">—</span>;
  if (typeof v === "boolean") return <span className="font-mono text-xs">{v ? "✓ yes" : "✗ no"}</span>;
  if (typeof v === "number" && MONEY_KEYS.test(k)) return <span className="tabular-nums font-medium">{fmtINR(v)}</span>;
  if (typeof v === "number") return <span className="tabular-nums">{v.toLocaleString("en-IN")}</span>;
  if (typeof v === "string" && DATE_KEYS.test(k) && /^\d{4}-\d{2}-\d{2}/.test(v)) return fmtDate(v);
  if (typeof v === "string") return <span className="break-words">{v}</span>;
  if (Array.isArray(v)) {
    if (v.length === 0) return <span className="text-muted-foreground">[]</span>;
    return <pre className="text-[10px] font-mono bg-muted/40 p-2 rounded-sm overflow-x-auto">{JSON.stringify(v, null, 2)}</pre>;
  }
  if (typeof v === "object") {
    return <pre className="text-[10px] font-mono bg-muted/40 p-2 rounded-sm overflow-x-auto">{JSON.stringify(v, null, 2)}</pre>;
  }
  return String(v);
}

function humanize(k) {
  return k.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
}

export default function RowDetailSheet({ row, onClose, title = "Details", testId = "row-detail-sheet" }) {
  if (!row) return null;
  const entries = Object.entries(row).filter(([k, v]) =>
    !HIDE_KEYS.has(k) && v !== null && v !== undefined && v !== "");
  // Pull a friendly title
  const label = row.booking_number || row.waitlist_number || row.gate_number
    || row.approval_number || row.rto_number || row.receipt_number
    || row.delivery_number || row.job_card_number || row.quote_number
    || row.lead_number || row.cancellation_number || row.finance_number
    || row.policy_number_local || row.name || row.invoice_number || row.bill_number;
  return (
    <Sheet open={!!row} onOpenChange={(o) => !o && onClose && onClose()}>
      <SheetContent side="right" className="w-full sm:max-w-lg rounded-none overflow-y-auto" data-testid={testId}>
        <SheetHeader className="pb-3 border-b">
          <SheetTitle className="font-heading flex items-center gap-2">
            {title}
            {label && <span className="text-xs font-mono text-muted-foreground">· {label}</span>}
          </SheetTitle>
        </SheetHeader>
        <div className="mt-4 grid grid-cols-1 gap-x-4 gap-y-2 text-sm">
          {entries.map(([k, v]) => (
            <div key={k} className="grid grid-cols-[140px_1fr] gap-3 border-b border-border/40 py-1.5">
              <div className="text-[10px] uppercase font-mono text-muted-foreground">{humanize(k)}</div>
              <div>{renderValue(k, v)}</div>
            </div>
          ))}
        </div>
      </SheetContent>
    </Sheet>
  );
}
